import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const SIDE_ITEMS = [
  {
    label: "Turn 180",
    sub: "180-Day Challenge",
    href: "/challenge",
    tag: "SIGNATURE",
    featured: true,
  },
  {
    label: "Fat & Nasty",
    sub: "Weekly Callout",
    href: "/challenge#pillar-3",
    tag: "WEEKLY",
  },
  {
    label: "Your Book",
    sub: "24-Week Journal",
    href: "/challenge#pillar-4",
    tag: "WRITE",
  },
  {
    label: "Ultimate Warrior",
    sub: "Accountability Feed",
    href: "/challenge#pillar-5",
    tag: "SHARE",
  },
  {
    label: "The Covenant",
    sub: "Sign & Commit",
    href: "/challenge#commit",
    tag: "COMMIT",
  },
];

/**
 * Persistent right-edge tab.
 * Click → opens a dropdown panel with Challenge options.
 */
export const ChallengeSideTab = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleItem = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    // Route + hash handling
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      navigate(path || "/");
      // wait a tick for route mount then scroll
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      {/* The tab button */}
      <button
        type="button"
        data-testid="side-tab-challenge"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((s) => !s)}
        className="group fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden md:flex"
      >
        <div
          className={`relative flex items-center gap-3 pl-3 pr-3 py-6 border border-white/15 border-r-0 backdrop-blur-md transition-all duration-500 rounded-l-md ${
            open
              ? "bg-silver-100 text-black"
              : "bg-black/70 text-silver-100 hover:bg-silver-100 hover:text-black"
          }`}
        >
          {/* Blinking dot */}
          <span
            className={`absolute -left-1.5 top-4 w-2 h-2 rounded-full animate-pulse ${
              open ? "bg-black" : "bg-silver-100 group-hover:bg-black"
            }`}
          />
          <div
            className="font-mono text-[10px] tracking-[0.5em] uppercase"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Turn 180 · Challenge
          </div>
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            className={`transition-transform duration-500 ${
              open ? "rotate-180" : "rotate-90"
            }`}
          >
            <path
              d="M1 6H11M11 6L6 1M11 6L6 11"
              stroke="currentColor"
              strokeWidth="1.2"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown panel + backdrop */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop (mobile-friendly dismiss) */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] hidden md:block"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              key="panel"
              ref={panelRef}
              data-testid="side-tab-dropdown"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-14 top-1/2 -translate-y-1/2 z-50 w-[340px] hidden md:block"
              role="menu"
              aria-label="Challenge menu"
            >
              <div className="relative bg-black border border-white/12 rounded-md shadow-[0_30px_100px_rgba(0,0,0,0.6)] overflow-hidden">
                {/* Grain */}
                <div className="absolute inset-0 grain-overlay opacity-[0.08] pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-start justify-between p-6 pb-4 border-b border-white/10">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                      §CHALLENGE
                    </div>
                    <div className="mt-2 font-serif text-white text-2xl leading-tight">
                      Turn <span className="italic text-silver-100">180.</span>
                    </div>
                    <div className="mt-1 text-white/50 text-[12px] leading-snug">
                      Choose your entry point.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                    className="p-1.5 rounded-full hover:bg-white/5 text-white/60 hover:text-white transition-colors"
                  >
                    <X className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>

                {/* Items */}
                <nav className="relative p-2">
                  {SIDE_ITEMS.map((it, i) => (
                    <motion.a
                      key={it.label}
                      href={it.href}
                      onClick={handleItem(it.href)}
                      data-testid={`side-tab-item-${i}`}
                      role="menuitem"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.05 }}
                      className={`flex items-center justify-between gap-4 px-4 py-3.5 rounded-md transition-colors duration-300 group ${
                        it.featured
                          ? "bg-white/[0.03] hover:bg-silver-100 hover:text-black border border-silver-100/25"
                          : "hover:bg-white/5"
                      }`}
                    >
                      <div className="min-w-0">
                        <div
                          className={`font-serif text-lg leading-tight ${
                            it.featured
                              ? "text-white group-hover:text-black"
                              : "text-white"
                          }`}
                        >
                          {it.label}
                        </div>
                        <div
                          className={`text-[12px] mt-0.5 ${
                            it.featured
                              ? "text-white/60 group-hover:text-black/70"
                              : "text-white/50"
                          }`}
                        >
                          {it.sub}
                        </div>
                      </div>
                      <span
                        className={`font-mono text-[9px] tracking-[0.32em] shrink-0 ${
                          it.featured
                            ? "text-silver-100 group-hover:text-black/70"
                            : "text-silver-300/60"
                        }`}
                      >
                        {it.tag}
                      </span>
                    </motion.a>
                  ))}
                </nav>

                {/* CTA foot */}
                <div className="relative border-t border-white/10 p-4">
                  <a
                    href="/challenge#commit"
                    onClick={handleItem("/challenge#commit")}
                    data-testid="side-tab-commit-cta"
                    className="flex items-center justify-between px-4 py-3 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500"
                  >
                    <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
                      Sign the Covenant
                    </span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M1 6H11M11 6L6 1M11 6L6 11"
                        stroke="currentColor"
                        strokeWidth="1.2"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChallengeSideTab;
